// apps/frontend/src/components/auth/SignupBonusWelcome.tsx
//
// Affiche un toast festif (mais doux, pas SaaS) apres un signup qui a
// declenche une campagne (ex: 100 credits IA offerts via /famille).
//
// Mecanique : useAuth ecrit localStorage 'glowp_signup_bonus' quand
// le backend renvoie data.campaignBonus. Ce composant lit la cle sur
// mount, l'affiche une seule fois, puis la supprime. TTL 7j pour
// supporter le cycle de verification email (lien clique = nouvel
// onglet = ancienne sessionStorage perdue). Cf. audit 03/06/2026 :
// 3 fondatrices /famille n'avaient JAMAIS vu cet ecran a cause de
// l'ancien sessionStorage, donc aucun push vers la publication.

import React, { useEffect, useState } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

interface CampaignBonus {
  credits: number;
  campaignKey: string;
  remainingSlots: number;
  expiresAt?: string | null;
  expiresInDays?: number | null;
  capturedAt?: number;
}

const STORAGE_KEY = 'glowp_signup_bonus';
// Flag pose quand le user a vu OU explicitement ferme la modale, pour
// ne plus declencher le fallback API a chaque session (sinon on lui
// jette l'ecran a la figure chaque login alors qu'il a deja vu).
const DISMISS_KEY = 'glowp_signup_bonus_dismissed';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const SignupBonusWelcome: React.FC = () => {
  // Pas de useNavigate ici : ce composant est monte hors du
  // RouterProvider (a cote de AppContent dans App.tsx), donc useNavigate
  // crashe -> page blanche (cf. feedback_no_blank_page). On utilise
  // window.location.assign pour la redirection vers /.
  const [bonus, setBonus] = useState<CampaignBonus | null>(null);

  useEffect(() => {
    // 1. Cas optimal : localStorage pose au moment de l'inscription
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CampaignBonus;
        if (parsed.capturedAt && Date.now() - parsed.capturedAt > TTL_MS) {
          localStorage.removeItem(STORAGE_KEY);
        } else if (
          parsed &&
          typeof parsed.credits === 'number' &&
          parsed.credits > 0
        ) {
          setBonus(parsed);
          localStorage.removeItem(STORAGE_KEY);
          return;
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }

    // 2. Fallback : si pas de localStorage (cas typique 'verify-email
    // ouvre un autre onglet'), interroge l'API. Si le user a un bonus
    // campagne actif et 0 publication, on declenche quand meme.
    // Pas de re-affichage si l'user a deja ferme une fois.
    if (localStorage.getItem(DISMISS_KEY)) return;
    const token = localStorage.getItem('glowp_access_token');
    if (!token) return;

    let cancelled = false;
    fetch(`${API_BASE_URL}/api/v1/users/me/pending-campaign-bonus`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const data = json?.data;
        if (data && typeof data.credits === 'number' && data.credits > 0) {
          setBonus(data as CampaignBonus);
        }
      })
      .catch(() => {
        /* offline / endpoint indispo -> on se tait, pas de drama */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!bonus) return null;

  const close = () => {
    setBonus(null);
    // Marque comme deja vu pour ne plus declencher le fallback API au
    // prochain login (sinon l'ecran reapparait a chaque session tant que
    // le user n'a pas publie son 1er article).
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };
  const goPublish = () => {
    close();
    // App.tsx ouvre la PublishChoiceModal (manuel vs bulk import) quand
    // ce flag est set au prochain pass d'effet auth -> on s'aligne sur
    // le pattern existant plutot que de creer une route bulk-import.
    try {
      localStorage.setItem('glowp_sell_pending', 'true');
    } catch {
      /* ignore */
    }
    // Pas de useNavigate (composant hors RouterProvider) : on utilise
    // window.location.assign qui marche partout. Le full reload n'est
    // pas un probleme ici car on quitte juste apres signup.
    if (window.location.pathname !== '/') {
      window.location.assign('/');
    }
    // Le useEffect d'App.tsx (cf. l 1023) consomme 'glowp_sell_pending'
    // au prochain pass et ouvre la PublishChoiceModal.
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center px-4 sm:px-6 pb-6 sm:pb-0"
      style={{ background: 'rgba(45, 42, 38, 0.45)' }}
      onClick={close}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl border-2 shadow-2xl overflow-hidden relative animate-[fadeIn_0.3s_ease-out]"
        style={{ borderColor: '#C8A882' }}
        onClick={e => e.stopPropagation()}
      >
        <div
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(240,200,122,0.35), transparent 70%)',
          }}
        />

        <button
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-brand-100/60 transition-colors z-10"
          aria-label="Fermer"
        >
          <X className="w-4 h-4 text-brand-dark" />
        </button>

        <div className="relative p-6 sm:p-7">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #C8A882 0%, #d4b896 100%)',
              boxShadow: '0 6px 16px rgba(200,168,130,0.35)',
            }}
          >
            <Sparkles className="w-6 h-6 text-white" />
          </div>

          <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-warm mb-1.5">
            Bienvenue dans la famille Glowp
          </p>
          <h2 className="text-2xl font-bold text-brand-dark leading-tight mb-3">
            On t&apos;offre {bonus.credits} publications assistées.
          </h2>
          <p className="text-sm text-brand-warm leading-relaxed mb-5">
            De quoi publier ton dressing en un éclair. Tu prends tes photos,
            Glowp détecte chaque article, range, écrit la description, fixe la
            taille — tu valides et c&apos;est en ligne.
          </p>

          <div className="rounded-2xl bg-brand-50 border border-brand-200/50 p-3.5 mb-5">
            <p className="text-[12px] text-brand-warm leading-relaxed">
              <span className="font-semibold text-brand-dark">
                À utiliser pour :
              </span>{' '}
              import en masse, détection multi-articles, génération de
              descriptions, retouche photo, suppression d&apos;arrière-plan.
            </p>
            {bonus.expiresInDays ? (
              <p className="text-[11px] text-brand-warm/85 leading-relaxed mt-2.5 pt-2.5 border-t border-brand-200/50">
                Valables {bonus.expiresInDays} jours — de quoi publier ton
                premier dressing sans pression, mais sans laisser dormir non
                plus.
              </p>
            ) : null}
          </div>

          <button
            onClick={goPublish}
            className="w-full px-5 py-3.5 rounded-2xl font-semibold text-white text-sm shadow-md inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            style={{
              background: 'linear-gradient(135deg, #C8A882 0%, #d4b896 100%)',
              boxShadow: '0 4px 16px rgba(200,168,130,0.35)',
            }}
          >
            Publier mon premier dressing
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={close}
            className="w-full mt-2 px-5 py-2.5 rounded-2xl font-medium text-sm text-brand-warm hover:bg-brand-100/40 transition-colors"
          >
            Plus tard
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default SignupBonusWelcome;
